import * as React from "react";
import { DataGridPremium, GridRenderCellParams, GridRowId, useGridApiRef } from '@mui/x-data-grid-premium';
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import { Alert, Stack } from "@mui/material";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import { useArchiveOrUnArchiveReportMutation, useGetCurrentUserCreatedReportsQuery } from "../../../services/cclTokenedGroupReportsApi";
import CclCommonLink from "../../../components/common/cclCommonLink";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { AccessEventListDetails } from "../../../services/types/accessEventTypes";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";

export type MyReportsScreenProps = {
};

export const MyReportsScreen: React.FC = (props) => {
    
    const apiRef = useGridApiRef();

    const claimservice = new UserClaimsService();
    const { logEvent } = useLogAccessEvent();

    let email = claimservice.GetCurrentUserEmail();

    const reportRequest = {
        username: email.split("@")[0].toUpperCase(),
        archived: false,
    };

    const { data: userReports } = useGetCurrentUserCreatedReportsQuery(reportRequest);
    const [archiveReport] = useArchiveOrUnArchiveReportMutation();

    const [openSnackbar, setOpenSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<"error" | "warning" | "info" | "success" | undefined>(undefined);

    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);

    const updateSelectedRows = (rows: any[] | undefined) => {
        if (!rows) return;
        let rowsMap: Map<GridRowId, any> = apiRef.current.getSelectedRows();
        let rowArray: any[] = [];
        for (let row of Array.from(rowsMap.values())) {
            rowArray.push(row);
        }
        setSelectedRows(rowArray);
    };

    const archiveSelectedReports = () => {
        let archiveReports = selectedRows.map((report) => {
            return report.gR_ID;
        });

        let request = {
            isArchived: true,
            body: archiveReports,
        }
        
        archiveReport(request)
            .unwrap()
            .then(() => {
                const eventDetails: AccessEventListDetails = {
                    listName: selectedRows[0].sgR_Name,
                }
                logEvent("GroupReportArchived", eventDetails);
                handleSnackbar("success", "Report archived");
            })
            .catch((error) => {
                console.error("Failed to archive the report: ", error);
                handleSnackbar("error", "Report archive failed");
            });
    }

    const handleSnackbar = (severity: string, message: string) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity as "error" | "warning" | "info" | "success" | undefined);
        setOpenSnackbar(true);
    };

    const COLUMNS = [
        {
            field: "gR_ID",
            headerName: "ID",
            type: "string",
            flex: 0.1,
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/assessments/group-reports/${params.row.gR_ID}`}
                />
            ),
        },
        {
            field: "reportFileName",
            headerName: "File",
            type: "string",
            flex: 0.5,
        },
        {
            field: "sgR_Name",
            headerName: "Assessment",
            type: "string",
            flex: 1,
        },
        {
            field: "createdByDateTime",
            headerName: "Created",
            type: "string",
            flex: 1,
        },
    ];

    const downloadSelectedReport = () => {
        let report = selectedRows[0];

        window.open(`http://vgsoweb01/GroupReports/CFSReport/${report.reportFileName}`, '_blank');
    }

    const miscButtonClick = (id: string) => {
        switch (id) {
            case "download-report-button":
                downloadSelectedReport();
                break;
            case "archive-report-button":
                    archiveSelectedReports();
                break;
            default:
                break;
        }
    };

    return (
        <Stack
            direction={"column"}
            spacing={2}
            width={"100%"}
        >
            <Alert
                variant="outlined"
                severity="info"
            >
                This is a prototype replacement for the Group Reports Application for user testing purposes only. Please continue to use the Group Reports Application
            </Alert>
            <DataGridPremium
                columns={COLUMNS}
                rows={userReports ?? []}
                apiRef={apiRef}
                getRowId= {(row) => row.gR_ID}
                components={{ Toolbar: CclDataGridToolbar }}
                componentsProps={{
                    toolbar: {
                        showSaveState: false,
                        hideSettingsButtons: true,
                        savestate: null,
                        clearstate: null,
                        miscButtons:
                            [
                                {
                                    id: "download-report-button",
                                    caption: "Download Report",
                                    onClick: () => { },
                                    returnSelected: false,
                                    restricted: true,
                                    disable:
                                        selectedRows === undefined ||
                                        selectedRows.length > 1 ||
                                        selectedRows.length === 0,
                                },
                                {
                                    id: "archive-report-button",
                                    caption: "Archive Report",
                                    onClick: () => { },
                                    returnSelected: true,
                                    restricted: true,
                                    disable:
                                        selectedRows === undefined ||
                                        selectedRows.length > 1 ||
                                        selectedRows.length === 0,
                                },
                            ],
                        btnClick: miscButtonClick,
                        anySelected: null,
                    },
                }}
                checkboxSelection
                disableSelectionOnClick={false}
                onSelectionModelChange={(rows: any[] | undefined) => updateSelectedRows(rows)}
            />

            <CclAlertSnackbar 
                open={openSnackbar} 
                message={snackbarMessage} 
                severity={snackbarSeverity} 
                onClose={() => setOpenSnackbar(false)} 
            />
        </Stack>
    );
};

export default MyReportsScreen;
